import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const ViewEmployeeProfile = () => {
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadProfile();
    }, [employeeId]);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await companyService.getEmployeeProfile(employeeId);
            const profileData = response?.data || response;
            setProfile(profileData);
        } catch (error) {
            console.error('Error loading employee profile:', error);
                const message = error?.response?.data?.message || 'Failed to load employee profile';
            toast.error(message);
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const months = locale === 'ar'
            ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
            : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    const downloadCV = (cvFile) => {
        if (!cvFile) {
            toast.error(t('employee.profile.noCvText') || 'No CV available');
            return;
        }
        // CV file is already a full URL from the API (asset('storage/' . $this->cv_file))
        const url = cvFile.startsWith('http') ? cvFile : `${STORAGE_BASE_URL}/${cvFile}`;
        window.open(url, '_blank');
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!profile) {
        return <div className="text-center py-4" dir={locale === 'ar' ? 'rtl' : 'ltr'}>{t('employee.vacancyDetail.vacancyNotFound') || 'Profile not found'}</div>;
    }

    const employee = profile;
    const userImageRaw = employee.user?.image;
    const userImage = userImageRaw ? (userImageRaw.startsWith('http') ? userImageRaw : `${STORAGE_BASE_URL}/${userImageRaw}`) : null;
    const userName = employee.user?.name || 'اسم المستخدم';
    const jobTitle = employee.job_title?.name || 'المسمي الوظيفي';
    const bio = employee.bio || '';
    const email = employee.user?.email || '';
    const phone = employee.user?.phone || '';
    const cvFile = employee.cv_file;
    const skills = employee.skills || [];
    const jobHistories = employee.job_histories || [];
    const educationHistories = employee.education_histories || [];

    return (
        <div className="employee-profile-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <CompanyNav user={user} />

            {/* Header */}
            <div className="px-3 mb-4 mt-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="back-btn"
                        style={{
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div>
                        <h3 className="section-title mb-1">{t('company.vacancyApplications.applicant') || 'Employee Profile'}</h3>
                    </div>
                </div>
            </div>

            {/* Profile Picture and Name */}
            <div className="profile-picture-container d-flex justify-content-center flex-wrap mb-4">
                <div className="profile-picture-large position-relative">
                    {userImage ? (
                        <img
                            src={userImage}
                            alt={userName}
                            className="profile-img-large"
                        />
                    ) : (
                        <div className="profile-img-large text-center align-content-center">
                            <b style={{ fontSize: '4rem' }}>{userName.charAt(0).toUpperCase()}</b>
                        </div>
                    )}
                </div>
                <p className="d-block w-100 text-center">{userName}</p>
                {jobTitle && (
                    <p className="d-block w-100 text-center text-muted" style={{ fontSize: '0.9rem' }}>{jobTitle}</p>
                )}
            </div>

            {/* Contact Information */}
            {(email || phone) && (
                <div className="profile-section px-3 mb-4">
                    <h3 className="section-title mb-3">{t('employee.profile.contactInformation') || 'Contact Information'}</h3>
                    <ul className="education-details">
                        {email && <li>{email}</li>}
                        {phone && <li>{phone}</li>}
                    </ul>
                </div>
            )}

            {/* Bio Section */}
            {bio && (
                <div className="profile-section px-3 mb-4">
                    <h3 className="section-title mb-3">{t('employee.profile.aboutMe') || 'About'}</h3>
                    <p className="section-text">{bio}</p>
                </div>
            )}

            {/* Skills Section */}
            {skills.length > 0 && (
                <div className="profile-section px-3 mb-4">
                    <h3 className="section-title mb-3">{t('employee.profile.skills') || 'Skills'}</h3>
                    <div className="d-flex flex-wrap gap-2">
                        {skills.map((skill) => (
                            <span
                                key={skill.id}
                                className="status-badge status-badge-accepted"
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}
                            >
                                {skill.name}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Job History Section */}
            {jobHistories.length > 0 && (
                <div className="profile-section px-3 mb-4">
                    <h3 className="section-title mb-3">{t('employee.profile.workExperience') || 'Work Experience'}</h3>
                    <div className="education-list">
                        {jobHistories.map((job, index) => (
                            <div key={job.id}>
                                <div className="education-item">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <h4 className="education-title">{job.company_name}</h4>
                                            {job.job_title && (
                                                <p className="education-subtitle">{job.job_title.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <ul className="education-details">
                                        <li>
                                            {t('employee.profile.from') || 'From'} {formatDate(job.from_date)} {t('employee.profile.until') || 'Until'} {job.to_date ? formatDate(job.to_date) : (t('employee.profile.untilNow') || 'Now')}
                                        </li>
                                        {job.description && <li>{job.description}</li>}
                                    </ul>
                                </div>
                                {index < jobHistories.length - 1 && <hr className="my-2" />}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Education History Section */}
            {educationHistories.length > 0 && (
                <div className="profile-section px-3 mb-4">
                    <h3 className="section-title mb-3">{t('employee.profile.educationHistory') || t('employee.profile.education') || 'Education'}</h3>
                    <div className="education-list">
                        {educationHistories.map((edu, index) => (
                            <div key={edu.id}>
                                <div className="education-item">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <h4 className="education-title">{edu.academic_degree} {edu.specialization ? `- ${edu.specialization}` : ''}</h4>
                                        </div>
                                    </div>
                                    <ul className="education-details">
                                        <li>
                                            {t('employee.profile.from') || 'From'} {formatDate(edu.from_date)} {t('employee.profile.until') || 'Until'} {edu.to_date ? formatDate(edu.to_date) : (t('employee.profile.untilNow') || 'Now')}
                                        </li>
                                    </ul>
                                </div>
                                {index < educationHistories.length - 1 && <hr className="my-2" />}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* CV Section */}
            {cvFile && (
                <div className="profile-section px-3 mb-5">
                    <h3 className="section-title mb-3">{t('employee.profile.cv') || 'CV/Resume'}</h3>
                    <div
                        className="cv-card"
                        style={{ cursor: 'pointer' }}
                        onClick={() => downloadCV(cvFile)}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="cv-info flex-grow-1">
                                <div className="cv-name">{t('employee.profile.viewCv') || t('employee.profile.cv') || 'View CV'}</div>
                            </div>
                            <div className="cv-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#DC2626">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <CompanyBottomNavigation />
        </div>
    );
};

export default ViewEmployeeProfile;

